import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
    MessageSquare,
    Users,
    CheckCircle,
    XCircle,
    AlertCircle,
    Eye,
    EyeOff,
    Save,
    RefreshCw,
} from 'lucide-react';
import { useAuthStore } from '@dukene/shared';
import { WhatsAppLogo } from '../../components/icons/BrandIcons';
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

// Validation schema for full WhatsApp API configuration
const whatsappConfigSchema = z.object({
    phoneNumberId: z.string().optional(),
    businessAccountId: z.string().optional(),
    accessToken: z.string().optional(),
    webhookVerifyToken: z.string().optional(),
    enabled: z.boolean(),
    displayPhoneNumber: z.string().optional(),
    bubbleMessage: z.string().optional(),
    bubbleWelcomeMessage: z.string().optional(),
    bubblePrefilledMessage: z.string().optional(),
    bubblePhoneSource: z.enum(['MANUAL', 'META']),
    bubbleEnabled: z.boolean(),
    autoReply: z.boolean(),
});

// Validation schema for chat bubble only
const chatBubbleSchema = z.object({
    displayPhoneNumber: z.string().optional(),
    bubbleWelcomeMessage: z.string().optional(),
    bubblePrefilledMessage: z.string().optional(),
    bubblePhoneSource: z.enum(['MANUAL', 'META']),
    bubbleEnabled: z.boolean(),
}).superRefine((data, ctx) => {
    if (data.bubbleEnabled && data.bubblePhoneSource === 'MANUAL' && !data.displayPhoneNumber?.trim()) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['displayPhoneNumber'],
            message: 'WhatsApp phone number is required',
        });
    }
});

type WhatsAppConfigFormData = z.infer<typeof whatsappConfigSchema>;

interface WhatsAppStats {
    totalConversations: number;
    activeConversations: number;
    totalMessages: number;
    totalContacts: number;
    intentAccuracy: number;
    avgResponseTime: number;
}

const WhatsAppConfiguration = () => {
    const activeStoreId = useAuthStore((state) => state.activeStoreId);
    const storeId = activeStoreId;

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [connectionStatus, setConnectionStatus] = useState<{
        status: 'CONNECTED' | 'DISCONNECTED' | 'PENDING' | 'LOADING';
        details?: any;
    }>({ status: 'LOADING' });
    const [showSuccessModal, setShowSuccessModal] = useState(false);

    const [config, setConfig] = useState<any>(null);
    const [stats, setStats] = useState<WhatsAppStats | null>(null);
    const [isMetaConnected, setIsMetaConnected] = useState(false);
    const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
    const [showAccessToken, setShowAccessToken] = useState(false);
    const [showVerifyToken, setShowVerifyToken] = useState(false);

    const {
        register,
        handleSubmit,
        watch,
        reset,
        setValue,
    } = useForm<WhatsAppConfigFormData>({
        resolver: zodResolver(whatsappConfigSchema),
        defaultValues: {
            enabled: false,
            bubbleEnabled: true,
            bubblePhoneSource: 'MANUAL',
            autoReply: true,
        },
    });

    const isEnabled = watch('enabled');

    const fetchConfig = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/whatsapp/config/${storeId}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
                    'X-Store-ID': storeId || '',
                },
            });

            if (response.ok) {
                const data = await response.json();
                setConfig(data);
                reset(data);
                setIsMetaConnected(!!(data.phoneNumberId && data.businessAccountId));

                // Verify connection if we have credentials
                if (data.phoneNumberId) {
                    verifyConnection();
                } else {
                    setConnectionStatus({ status: 'DISCONNECTED' });
                }
            } else {
                setConnectionStatus({ status: 'DISCONNECTED' });
            }
        } catch (error) {
            console.error('Error fetching WhatsApp config:', error);
            setConnectionStatus({ status: 'DISCONNECTED' });
        } finally {
            setLoading(false);
        }
    };

    const verifyConnection = async () => {
        setVerifying(true);
        try {
            const response = await fetch(`${API_BASE_URL}/whatsapp/config/${storeId}/verify`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
                    'X-Store-ID': storeId || '',
                },
            });
            const data = await response.json();
            setConnectionStatus({
                status: data.status,
                details: data
            });
            if (data.status === 'CONNECTED') {
                setConfig((current: any) => ({
                    ...current,
                    metaDisplayPhoneNumber: data.displayPhoneNumber,
                    metaVerifiedName: data.verifiedName,
                    metaProfilePictureUrl: data.profilePictureUrl,
                }));
            }
        } catch (error) {
            setConnectionStatus({ status: 'DISCONNECTED' });
        } finally {
            setVerifying(false);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/whatsapp/stats/${storeId}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
                    'X-Store-ID': storeId || '',
                },
            });

            if (response.ok) {
                const data = await response.json();
                setStats(data);
            }
        } catch (error) {
            console.error('Error fetching WhatsApp stats:', error);
        }
    };

    useEffect(() => {
        if (storeId) {
            fetchConfig();
            fetchStats();
        }

        // Check for success redirect
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('meta_connected') === 'true') {
            setShowSuccessModal(true);
            // Clean up URL
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }, [storeId]);

    const onSubmit = async (data: WhatsAppConfigFormData) => {
        setSaving(true);
        try {
            const response = await fetch(`${API_BASE_URL}/whatsapp/config/${storeId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
                    'X-Store-ID': storeId || '',
                },
                body: JSON.stringify(data),
            });

            if (response.ok) {
                const updated = await response.json();
                setConfig(updated);
                alert('WhatsApp configuration saved successfully!');
            } else {
                alert('Failed to save configuration');
            }
        } catch (error) {
            console.error('Error saving WhatsApp config:', error);
            alert('Error saving configuration');
        } finally {
            setSaving(false);
        }
    };

    const saveChatBubble = async () => {
        const bubbleData = {
            displayPhoneNumber: watch('displayPhoneNumber'),
            bubbleWelcomeMessage: watch('bubbleWelcomeMessage'),
            bubblePrefilledMessage: watch('bubblePrefilledMessage'),
            bubblePhoneSource: watch('bubblePhoneSource'),
            bubbleEnabled: watch('bubbleEnabled'),
        };

        // Validate chat bubble data
        const validation = chatBubbleSchema.safeParse(bubbleData);
        if (!validation.success) {
            alert(validation.error.issues[0]?.message || 'Please check the chat bubble settings');
            return;
        }

        setSaving(true);
        try {
            const response = await fetch(`${API_BASE_URL}/whatsapp/config/${storeId}/bubble`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
                    'X-Store-ID': storeId || '',
                },
                body: JSON.stringify(bubbleData),
            });

            if (response.ok) {
                const updated = await response.json();
                setConfig(updated);
                alert('Chat bubble settings saved successfully!');
            } else {
                alert('Failed to save chat bubble settings');
            }
        } catch (error) {
            console.error('Error saving chat bubble:', error);
            alert('Error saving chat bubble settings');
        } finally {
            setSaving(false);
        }
    };

    const handleMetaConnect = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/meta/oauth/initiate?returnTo=/store/whatsapp`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
                    'X-Store-ID': storeId || '',
                },
            });
            const data = await response.json();
            if (data.authUrl) {
                window.location.href = data.authUrl;
            }
        } catch (error) {
            console.error('Failed to initiate Meta OAuth:', error);
            alert('Failed to initiate Meta connection');
        }
    };

    if (loading) {
        return (
            <div className="flex h-64 items-center justify-center">
                <div className="w-8 h-8 border-2 border-[#006838] border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6 pb-16 max-w-4xl">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 pb-4 border-b border-[#D2D5D9] dark:border-slate-800">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-[#E3F1DF] rounded-lg flex items-center justify-center">
                            <WhatsAppLogo className="w-6 h-6" />
                        </div>
                        <div>
                            <h1 className="text-xl font-semibold text-[#202223] dark:text-white">WhatsApp Chat</h1>
                            <p className="text-sm text-[#5C5F62] dark:text-slate-400 mt-0.5">Manage the WhatsApp chat bubble shown on your storefront.</p>
                        </div>
                    </div>

                    {/* Status Badge */}
                    {config && (
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-700">
                            {watch('bubbleEnabled') ? (
                                <>
                                    <CheckCircle className="w-5 h-5 text-green-600" />
                                    <span className="text-xs font-semibold text-green-700">Bubble Active</span>
                                </>
                            ) : (
                                <>
                                    <XCircle className="w-5 h-5 text-gray-400" />
                                    <span className="text-xs font-semibold text-gray-600">Bubble Inactive</span>
                                </>
                            )}
                        </div>
                    )}
            </div>

            {/* Stats Cards */}
            {stats && isEnabled && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <div className="bg-white dark:bg-[#1A1D21] rounded-xl border border-[#D2D5D9] dark:border-slate-800 p-4">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-gray-600 text-sm">Total Conversations</span>
                            <MessageSquare className="w-5 h-5 text-blue-600" />
                        </div>
                        <p className="text-xl font-semibold text-gray-900 dark:text-white">{stats.totalConversations}</p>
                        <p className="text-sm text-green-600 mt-1">{stats.activeConversations} active</p>
                    </div>

                    <div className="bg-white dark:bg-[#1A1D21] rounded-xl border border-[#D2D5D9] dark:border-slate-800 p-4">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-gray-600 text-sm">Total Messages</span>
                            <WhatsAppLogo className="w-5 h-5" />
                        </div>
                        <p className="text-xl font-semibold text-gray-900 dark:text-white">{stats.totalMessages}</p>
                    </div>

                    <div className="bg-white dark:bg-[#1A1D21] rounded-xl border border-[#D2D5D9] dark:border-slate-800 p-4">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-gray-600 text-sm">Contacts</span>
                            <Users className="w-5 h-5 text-orange-600" />
                        </div>
                        <p className="text-xl font-semibold text-gray-900 dark:text-white">{stats.totalContacts}</p>
                    </div>

                    <div className="bg-white dark:bg-[#1A1D21] rounded-xl border border-[#D2D5D9] dark:border-slate-800 p-4">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-gray-600 text-sm">Bot Accuracy</span>
                            <RefreshCw className="w-5 h-5 text-green-600" />
                        </div>
                        <p className="text-xl font-semibold text-gray-900 dark:text-white">{stats.intentAccuracy}%</p>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 gap-5">
                {/* Meta Connection Section */}
                <div className="hidden">
                    <div className="p-4 border-b border-[#E1E4E8] dark:border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-[#F6F6F7]/50 dark:bg-slate-900/40">
                        <div>
                            <h2 className="text-sm font-semibold text-[#202223] dark:text-white">Meta Business Connection</h2>
                            <p className="text-sm text-gray-500">Connect your WhatsApp Business assets automatically</p>
                        </div>
                        {isMetaConnected && (
                            <div className="flex flex-wrap gap-4 items-center">
                                <div className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                                    <CheckCircle className="w-4 h-4" />
                                    Connected via Meta OAuth
                                </div>
                                
                                {/* Connection Health Badge */}
                                <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
                                    connectionStatus.status === 'CONNECTED' ? 'bg-blue-100 text-blue-700' :
                                    connectionStatus.status === 'PENDING' ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-red-100 text-red-700'
                                }`}>
                                    {verifying ? (
                                        <RefreshCw className="w-3 h-3 animate-spin" />
                                    ) : connectionStatus.status === 'CONNECTED' ? (
                                        <CheckCircle className="w-4 h-4" />
                                    ) : (
                                        <AlertCircle className="w-4 h-4" />
                                    )}
                                    Health: {connectionStatus.status}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="p-5">
                        {!isMetaConnected ? (
                            <div className="text-center py-5">
                                <div className="w-12 h-12 bg-[#E3F1DF] text-[#006838] rounded-xl flex items-center justify-center mx-auto mb-4">
                                    <WhatsAppLogo className="w-7 h-7" />
                                </div>
                                <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">Connect Meta Business</h3>
                                <p className="text-sm text-gray-600 dark:text-slate-400 max-w-md mx-auto mb-5">
                                    Skip the manual setup. Connect your Meta account to automatically sync your WhatsApp Business phone number and catalog.
                                </p>
                                <button
                                    onClick={handleMetaConnect}
                                    className="h-9 px-4 bg-[#006838] text-white rounded-lg text-xs font-semibold hover:bg-[#00522c] transition-colors flex items-center gap-2 mx-auto"
                                >
                                    <MessageSquare className="w-5 h-5" />
                                    Connect with Meta
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-8">
                                {/* Success Info */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="p-6 bg-gray-50 rounded-2xl border border-gray-100">
                                        <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Messaging Settings</h4>
                                        <div className="space-y-4">
                                            <div className="flex items-center justify-between p-3 bg-white rounded-xl border border-gray-100">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 bg-green-100 text-green-600 rounded-lg flex items-center justify-center">
                                                        <WhatsAppLogo className="w-5 h-5" />
                                                    </div>
                                                    <span className="font-medium text-gray-700">Accept Messages</span>
                                                </div>
                                                <input
                                                    type="checkbox"
                                                    {...register('enabled')}
                                                    className="w-5 h-5 text-blue-600 rounded"
                                                />
                                            </div>
                                            <div className="flex items-center justify-between p-3 bg-white rounded-xl border border-gray-100">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 bg-purple-100 text-purple-600 rounded-lg flex items-center justify-center">
                                                        <RefreshCw className="w-4 h-4" />
                                                    </div>
                                                    <span className="font-medium text-gray-700">AI Auto-Reply</span>
                                                </div>
                                                <input
                                                    type="checkbox"
                                                    {...register('autoReply')}
                                                    className="w-5 h-5 text-blue-600 rounded"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="p-6 bg-gray-50 rounded-2xl border border-gray-100">
                                        <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Meta Identifiers</h4>
                                        <div className="space-y-3">
                                            <div className="flex items-center justify-between text-sm">
                                                <span className="text-gray-500">Phone Number ID</span>
                                                <span className="font-mono text-gray-900 bg-white px-2 py-1 rounded border border-gray-100">{config?.phoneNumberId}</span>
                                            </div>
                                            <div className="flex items-center justify-between text-sm">
                                                <span className="text-gray-500">Business ID</span>
                                                <span className="font-mono text-gray-900 bg-white px-2 py-1 rounded border border-gray-100">{config?.businessAccountId}</span>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
                                            className="mt-6 text-sm text-blue-600 font-medium hover:underline flex items-center gap-1"
                                        >
                                            {isAdvancedOpen ? 'Hide' : 'Show'} Advanced Settings
                                        </button>
                                    </div>
                                </div>

                                {isAdvancedOpen && (
                                    <div className="p-6 bg-yellow-50 rounded-2xl border border-yellow-100 animate-in slide-in-from-top duration-300">
                                        <div className="flex items-center gap-2 mb-4 text-yellow-800">
                                            <AlertCircle className="w-5 h-5" />
                                            <h4 className="font-bold">Advanced Manual Configuration</h4>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Access Token</label>
                                                <div className="relative">
                                                    <input
                                                        type={showAccessToken ? 'text' : 'password'}
                                                        {...register('accessToken')}
                                                        className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg pr-10"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowAccessToken(!showAccessToken)}
                                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400"
                                                    >
                                                        {showAccessToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                                    </button>
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Webhook Verify Token</label>
                                                <div className="relative">
                                                    <input
                                                        type={showVerifyToken ? 'text' : 'password'}
                                                        {...register('webhookVerifyToken')}
                                                        className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg pr-10"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowVerifyToken(!showVerifyToken)}
                                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400"
                                                    >
                                                        {showVerifyToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Chat Bubble Section */}
                <div className="bg-white dark:bg-[#1A1D21] rounded-xl border border-[#D2D5D9] dark:border-slate-800 shadow-sm overflow-hidden">
                    <div className="p-4 border-b border-[#E1E4E8] dark:border-slate-800 bg-[#F6F6F7]/50 dark:bg-slate-900/40">
                        <h2 className="text-sm font-semibold text-[#202223] dark:text-white">Storefront Chat Bubble</h2>
                        <p className="text-sm text-gray-500">Configure the WhatsApp button that appears for your customers</p>
                    </div>

                    <div className="p-5">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <div className="flex items-center gap-3 p-3 bg-[#F6F6F7] dark:bg-slate-900 rounded-lg border border-[#E1E4E8] dark:border-slate-800">
                                    <input
                                        type="checkbox"
                                        {...register('bubbleEnabled')}
                                        className="w-4 h-4 text-[#006838] rounded"
                                    />
                                    <div>
                                        <label className="text-sm font-semibold text-gray-900 dark:text-white">Enable Chat Bubble</label>
                                        <p className="text-xs text-gray-500">Show the floating button on your storefront</p>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">WhatsApp account used by the bubble</label>
                                    <div className="grid gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setValue('bubblePhoneSource', 'MANUAL', { shouldDirty: true })}
                                            className={`flex items-center gap-3 rounded-lg border p-3 text-left transition-colors ${
                                                watch('bubblePhoneSource') !== 'META'
                                                    ? 'border-[#006838] bg-[#006838]/5'
                                                    : 'border-[#D2D5D9] dark:border-slate-700'
                                            }`}
                                        >
                                            <span className={`h-4 w-4 rounded-full border-4 ${
                                                watch('bubblePhoneSource') !== 'META'
                                                    ? 'border-[#006838]'
                                                    : 'border-gray-300'
                                            }`} />
                                            <div>
                                                <p className="text-sm font-semibold text-gray-900 dark:text-white">Custom WhatsApp number</p>
                                                <p className="text-xs text-gray-500">Use a number you enter below</p>
                                            </div>
                                        </button>

                                        <button
                                            type="button"
                                            disabled={!config?.metaDisplayPhoneNumber}
                                            onClick={() => setValue('bubblePhoneSource', 'META', { shouldDirty: true })}
                                            className={`flex items-center gap-3 rounded-lg border p-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                                                watch('bubblePhoneSource') === 'META'
                                                    ? 'border-[#006838] bg-[#006838]/5'
                                                    : 'border-[#D2D5D9] dark:border-slate-700'
                                            }`}
                                        >
                                            {config?.metaProfilePictureUrl ? (
                                                <img
                                                    src={config.metaProfilePictureUrl}
                                                    alt=""
                                                    className="h-9 w-9 rounded-full object-cover"
                                                />
                                            ) : (
                                                <WhatsAppLogo className="h-9 w-9 shrink-0" />
                                            )}
                                            <div className="min-w-0 flex-1">
                                                <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">
                                                    {config?.metaVerifiedName || 'Connected Meta WhatsApp profile'}
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                    {config?.metaDisplayPhoneNumber || 'No verified Meta WhatsApp profile available'}
                                                </p>
                                            </div>
                                            <span className={`h-4 w-4 rounded-full border-4 ${
                                                watch('bubblePhoneSource') === 'META'
                                                    ? 'border-[#006838]'
                                                    : 'border-gray-300'
                                            }`} />
                                        </button>
                                    </div>
                                </div>

                                {watch('bubblePhoneSource') !== 'META' && (
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Custom WhatsApp Number</label>
                                    <input
                                        type="text"
                                        {...register('displayPhoneNumber')}
                                        className="w-full h-10 px-3 border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-900 rounded-lg text-sm focus:ring-2 focus:ring-[#006838]/20 outline-none"
                                        placeholder="+254 712 345 678"
                                    />
                                </div>
                                )}

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Welcome Message</label>
                                    <textarea
                                        {...register('bubbleWelcomeMessage')}
                                        rows={3}
                                        className="w-full px-3 py-2 border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-900 rounded-lg text-sm focus:ring-2 focus:ring-[#006838]/20 outline-none"
                                        placeholder="Hi! How can we help you today?"
                                    />
                                    <p className="mt-1 text-xs text-gray-500">Shown as a message from your store when the chat opens.</p>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Customer Starter Message</label>
                                    <textarea
                                        {...register('bubblePrefilledMessage')}
                                        rows={3}
                                        className="w-full px-3 py-2 border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-900 rounded-lg text-sm focus:ring-2 focus:ring-[#006838]/20 outline-none"
                                        placeholder="Hi! I'm interested in your products."
                                    />
                                    <p className="mt-1 text-xs text-gray-500">Pre-filled for the customer; they can edit it before opening WhatsApp.</p>
                                </div>
                                
                                <button
                                    onClick={saveChatBubble}
                                    disabled={saving}
                                    className="w-full h-9 bg-[#006838] text-white rounded-lg text-xs font-semibold hover:bg-[#00522c] transition-colors flex items-center justify-center gap-2"
                                >
                                    {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                    Save Bubble Settings
                                </button>
                            </div>

                            <div className="relative">
                                <label className="block text-sm font-semibold text-gray-700 mb-4">Preview</label>
                                <div className="bg-[#F6F6F7] dark:bg-slate-900 rounded-lg aspect-video relative flex items-end justify-end p-5 border border-[#D2D5D9] dark:border-slate-800 overflow-hidden">
                                    <div className="absolute inset-0 flex items-center justify-center opacity-10 pointer-events-none">
                                        <div className="grid grid-cols-4 gap-4">
                                            {[...Array(12)].map((_, i) => <div key={i} className="w-12 h-12 bg-gray-400 rounded-lg"></div>)}
                                        </div>
                                    </div>
                                    
                                    {watch('bubbleEnabled') && (
                                        <div className="rounded-full shadow-2xl animate-bounce">
                                            <WhatsAppLogo className="w-14 h-14" />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="hidden">
                    <button
                        onClick={fetchConfig}
                        className="h-9 px-4 border border-[#D2D5D9] text-gray-600 rounded-lg text-xs font-semibold hover:bg-[#F6F6F7] transition-colors"
                    >
                        Reset Changes
                    </button>
                    <button
                        onClick={handleSubmit(onSubmit)}
                        disabled={saving}
                        className="h-9 px-4 bg-[#006838] text-white rounded-lg text-xs font-semibold hover:bg-[#00522c] transition-colors flex items-center gap-2"
                    >
                        {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        Save All Configuration
                    </button>
                </div>
            </div>

            {/* Success Modal */}
            {showSuccessModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center animate-in fade-in zoom-in duration-300">
                        <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                            <CheckCircle className="w-12 h-12" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">WhatsApp Connected!</h2>
                        <p className="text-gray-600 mb-8">
                            Your Meta Business Assets have been successfully synced. Your WhatsApp bot is now ready to assist your customers.
                        </p>
                        
                        <div className="space-y-4 mb-8">
                            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl text-left">
                                <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <MessageSquare className="w-4 h-4" />
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-gray-900">Messaging Enabled</p>
                                    <p className="text-xs text-gray-500">Customers can now reach you via WhatsApp</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl text-left">
                                <div className="w-8 h-8 bg-purple-100 text-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <Users className="w-4 h-4" />
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-gray-900">Catalog Synced</p>
                                    <p className="text-xs text-gray-500">Your inventory is now visible on WhatsApp</p>
                                </div>
                            </div>
                        </div>

                        <button
                            onClick={() => setShowSuccessModal(false)}
                            className="w-full py-3 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition-colors shadow-lg shadow-green-200"
                        >
                            Great, let's go!
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WhatsAppConfiguration;
